import React from 'react';
import styled from 'styled-components';
import { 
  Card, 
  CardBody, 
  Button, 
  OutlineButton 
} from '../../styles/StyledComponents';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const DialogCard = styled(Card)`
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  h3 {
    margin: 0;
    color: var(--secondary-color);
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--dark-gray);
  
  &:hover {
    color: var(--danger-color);
  }
`;

const DialogContent = styled.div`
  margin-bottom: 1.5rem;
  
  p {
    margin: 0 0 1rem 0;
    color: var(--text-color);
    line-height: 1.5;
  }
`;

const DialogActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SuccessIcon = styled.div`
  color: var(--success-color);
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const ErrorIcon = styled.div`
  color: var(--danger-color);
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const WarningIcon = styled.div`
  color: var(--warning-color);
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;
`;

const InfoIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  text-align: center;
  margin-bottom: 1rem;
`;

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
}

const CustomDialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  onConfirm,
  confirmText = 'OK',
  cancelText = 'Cancel',
  showCancel = true
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <SuccessIcon>✓</SuccessIcon>;
      case 'error':
        return <ErrorIcon>✗</ErrorIcon>;
      case 'warning':
        return <WarningIcon>⚠</WarningIcon>;
      case 'confirm':
        return <InfoIcon>?</InfoIcon>;
      default:
        return <InfoIcon>ℹ</InfoIcon>;
    }
  };

  const getConfirmButtonColor = () => {
    switch (type) {
      case 'success':
        return 'var(--success-color)';
      case 'error':
        return 'var(--danger-color)';
      case 'warning':
        return 'var(--warning-color)';
      case 'confirm':
        return 'var(--primary-color)';
      default:
        return 'var(--primary-color)';
    }
  };

  return (
    <Overlay onClick={onClose}>
      <DialogCard onClick={(e) => e.stopPropagation()}>
        <CardBody>
          <DialogHeader>
            <h3>{title}</h3>
            <CloseButton onClick={onClose}>&times;</CloseButton>
          </DialogHeader>
          
          {getIcon()}
          
          <DialogContent>
            <p>{message}</p>
          </DialogContent>
          
          <DialogActions>
            {showCancel && (
              <OutlineButton onClick={handleCancel}>
                {cancelText}
              </OutlineButton>
            )}
            <Button 
              onClick={handleConfirm}
              style={{ backgroundColor: getConfirmButtonColor() }}
            >
              {confirmText}
            </Button>
          </DialogActions>
        </CardBody>
      </DialogCard>
    </Overlay>
  );
};

export default CustomDialog; 